import {AfterContentInit, Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {PrlProfilesService} from '../../../../shared/services/prl-profiles.service';
import {FormControl, FormGroup} from '@angular/forms';
import {forkJoin, of, Subject} from 'rxjs';
import {catchError, first, switchMap, takeUntil, tap} from 'rxjs/operators';
import {
    FieldUiDataObj,
    GridColumn,
    KeyValueObj,
    MxButtonModel,
    MxDynamicFormService,
    MxFieldCustomizerService,
    MxGridButtonsConfig,
    MxGridComponent,
    MxGridCustomizerEvent,
    MxGridHandleOrderDefault,
    MxGridService,
    MxInformationTooltipConfig,
    MxPreloaderService,
    MxReportFieldsConverterService,
    OrderedIdNameMxObj,
    PreloaderConfig,
    ReportMxObj,
    ReportUiDataObj,
    TableMxObj,
    withPreloader
} from 'mx-core';

import {MOMENT_DATE_FORMAT_DAY} from '../../../../../../../mx-core/src/lib/consts/date-time-format-consts';
import {Moment} from 'moment';
import {map} from 'rxjs/internal/operators/map';
import {PrlLanguageService} from '../../../prl-language/services/prl-language.service';
import {PrlReportService} from '../../../../shared/services/prl-report.service';

const CURRENCY_TOOLTIP_REPORTS = ['Traffic Stats', 'Sub Affiliates Traffic Stats'];

@Component({
    selector: 'prl-statistics',
    templateUrl: './statistics.component.html',
    standalone: false,
    styleUrls: ['./statistics.component.scss'],
    providers: [MxFieldCustomizerService]
})
export class StatisticsComponent implements OnInit, AfterContentInit, OnDestroy {
    public isDataReady: boolean = false;

    protected form: FormGroup;
    protected selectedCampaignControl: FormControl = new FormControl('');
    protected selectedReportControl: FormControl = new FormControl('');
    protected runReportButton: MxButtonModel;
    protected exportReportButton: MxButtonModel;

    protected exportReportConfig: MxInformationTooltipConfig = {
        links: [],
        header: 'Help',
        content: 'label.export.to.csv.help'
    };

    protected currencyTooltipConfig: MxInformationTooltipConfig = {
        links: [],
        header: 'Help',
        content: 'label.stats.currency.help'
    };

    protected inputFields: FieldUiDataObj[];
    protected reports: Array<KeyValueObj> = [];
    protected currentCampaigns: Array<KeyValueObj> = [];
    protected allCampaigns: Array<KeyValueObj> = [];
    protected reportCampaigns: Map<string, Array<KeyValueObj>> = new Map();
    protected isMobile: boolean = false;

    protected isReportReady: boolean = false;
    protected gettingData: boolean = false;
    protected showFilters: boolean = true;
    protected firstLoadReport: boolean = true;

    @ViewChild(MxGridComponent, {static: true})
    private grid: MxGridComponent;

    private destroy$$: Subject<null> = new Subject<null>();
    private preloaderConfig: PreloaderConfig = {name: 'report', type: 'LinearSpinner'};
    private report: ReportMxObj;
    private table: TableMxObj;

    private gridService: MxGridService;

    constructor(
        private converterService: MxReportFieldsConverterService,
        private languageService: PrlLanguageService,
        private formService: MxDynamicFormService,
        private preloader: MxPreloaderService,
        private reportService: PrlReportService,
        private statisticsService: PrlProfilesService
    ) {
        this.form = new FormGroup({});
    }

    ngOnDestroy(): void {
        this.destroy$$.next(null);
    }

    ngOnInit(): void {
        this.runReportButton = {
            label: 'label.stats.run.report',
            tooltip: 'label.stats.run.report',
            className: 'mxs-button--primary',
            disabled: this.isRunReportDisabled,
            clickHandler: this.runReport
        };
        this.exportReportButton = {
            label: 'label.export.to.csv',
            tooltip: 'label.export.to.csv',
            className: 'mxs-button--primary',
            disabled: this.isRunReportDisabled,
            clickHandler: this.exportReport
        };

        this.statisticsService.getCampaigns()
            .pipe(
                takeUntil(this.destroy$$),
                map(campaigns => campaigns.map(c => ({
                    key: c.id,
                    value: c.name,
                    dependOn: null
                }) as KeyValueObj)),
                switchMap((allCampaigns) => {
                    const lang = this.languageService.getLanguage();

                    const campaignReports$ = allCampaigns.map(campaign =>
                        this.reportService.getCampaignReports(+campaign.key, lang).pipe(
                            catchError(err => {
                                console.error(err);
                                return of([]);
                            }),
                            tap((reports: OrderedIdNameMxObj[]) => {
                                if (reports.length > 0) {
                                    this.allCampaigns.push(campaign);
                                }
                                this.pushReports(reports, campaign);
                            })
                        )
                    );

                    const affiliateReports$ = this.reportService.getAffiliateReports(lang).pipe(
                        catchError(err => {
                            console.error(err);
                            return of([]);
                        }),
                        tap((reports:OrderedIdNameMxObj[]) => this.pushReports(reports))
                    );

                    return forkJoin([...campaignReports$, affiliateReports$]);
                }),
                withPreloader(this.preloader)
            )
            .subscribe({
                next: () => {
                },
                error: (err) => console.error(err),
                complete: () => {
                    if (!this.selectedReportControl.value && this.reports.length > 0) {
                        this.selectedReportControl.setValue(this.reports[0]);
                        this.onReportChanged();
                    }
                }
            });
    };

    ngAfterContentInit(): void {
        this.grid.customize.pipe(first()).subscribe((gridCustomizerEvent: MxGridCustomizerEvent) => {
            gridCustomizerEvent.customizeActions(MxGridButtonsConfig.hideDefault())
                .customizeEmptyGridParams(() => {
                    if (this.firstLoadReport) {
                        return {image: 'ghost_smile', text: 'label.table.nodata.first'};
                    } else {
                        return {image: 'ghost_sad', text: 'label.table.nodata'};
                    }
                })
                .customizeProps({
                    dateFormat: MOMENT_DATE_FORMAT_DAY,
                    selectionMode: 'none'
                })
                .customizeOrder(new MxGridHandleOrderDefault('order_col'))
                .onSetUp((service) => {
                    this.gridService = service;
                });
        });
    }

    onReportChanged(updateCampaigns: boolean = true): void {
        const stringKey = 'key';
        this.isReportReady = false;
        this.isDataReady = false;
        this.gridService.collection.setRows([]);
        this.gridService.collection.rowsChanged();

        const reportId = +this.selectedReportControl.value[stringKey];

        this.updateControls(updateCampaigns, reportId);

        const language = this.languageService.getLanguage();

        const campaignId = this.selectedCampaignControl.value ? +this.selectedCampaignControl.value[stringKey] : null;
        this.reportService.getReportObj(campaignId, reportId, language)
            .pipe(takeUntil(this.destroy$$),
                withPreloader(this.preloader, this.preloaderConfig)
            )
        .subscribe(report => {
            this.report = report;
            this.isReportReady = true;
            this.firstLoadReport = false;
            this.inputFields = report.input.map(value => {
                return this.converterService.convertToUiData(this.expandField(MxReportFieldsConverterService.convertToEntity(value)));
            });
            this.initForm();
        });
    }

    private expandField(field: ReportUiDataObj): ReportUiDataObj {
        return {...field, collapsed: false, sublist: field.sublist?.map(subField => this.expandField(subField))};
    }

    onCampaignChanged(): void {
        this.onReportChanged(false);
    }

    toggleFilters(): void {
        this.showFilters = !this.showFilters;
    }

    protected showCurrencyTooltip(): boolean {
        return CURRENCY_TOOLTIP_REPORTS.includes(this.report?.name);
    }

    private pushReports(reports: OrderedIdNameMxObj[], campaign: KeyValueObj = null): void {
        reports.forEach(report => {
            if (!this.reports.find(c => c.key === report.id)) {
                this.reports.push({
                    key: report.id,
                    value: report.name,
                    dependOn: null
                });
            }

            let reportCampaign = this.reportCampaigns.get(report.id);

            if (reportCampaign) {
                reportCampaign.push(campaign);
                this.reportCampaigns.set(report.id, reportCampaign);
            } else {
                this.reportCampaigns.set(report.id, [campaign]);
            }

        });
    }

    private initForm() {
        this.form = this.formService.buildForm(this.inputFields, 'edit', this.destroy$$);
        this.formService.fillForm(this.form, this.inputFields, null);
    }

    private isRunReportDisabled = (): boolean => {
        return this.form.invalid || !this.isReportReady || this.gettingData;
    };

    private runReport = (isCsv: boolean = false) => {
        this.gettingData = true;
        const index = 1;
        const ascending = true;
        const reportString = 'report';
        let sendingObj: any = {};
        sendingObj[reportString] = this.report;
        sendingObj[reportString]['language'] = this.languageService.getLanguage();

        if (this.inputFields != null) {
            sendingObj[reportString]['input'] = this.parseInputFields(this.inputFields);
        }

        sendingObj['sc'] = {
            'offset': 0,
            'order': [
                {
                    'ascending': ascending,
                    'column': index
                }
            ],
            'namedQuery': ''
        };

        this.reportService
            .runReportAtOnce(sendingObj)
            .pipe(takeUntil(this.destroy$$),
                withPreloader(this.preloader)
            ).subscribe(tableObj => {
            this.table = tableObj;
            const columns = tableObj.headers.map(header => {
                return new GridColumn({field: header.column, label: header.title});
            });
            const valuesLength = tableObj.values.length;
            const values: any[][] = Array.from({length: valuesLength}, () => []);
            const totals = new Map<string, string | number>();
            for (let header of tableObj.headers) {
                let headerIndex = tableObj.headers.indexOf(header);
                if (tableObj.total) {
                    totals.set(header.column, tableObj.total.data[headerIndex]);
                }
                for (let i = 0; i < valuesLength; i++) {
                    values[i][header.column] = tableObj.values[i].data[headerIndex];
                }
            }

            this.gridService.collection.setRows(values);
            this.gridService.grandTotals.setGrandTotals(totals);
            this.gridService.columns.setColumns(columns, true);
            this.isDataReady = true;

            if (isCsv) {
                this.exportToCSV();
            }
            this.gettingData = false;
        });
    };


    private exportToCSV() {
        if(!this.isDataReady) {
            return;
        }
        const headers = this.table.headers;
        const data = this.table.values;

        const headerRow = headers.map(value => value.title).join(',');
        const rows = data.map(row => Object.values(row).join(',')).join('\n');
        const csvContent = data.length > 0 ? headerRow + '\n' + rows : 'No data to display';

        const blob = new Blob([csvContent], {type: 'text/csv;charset=utf-8;'});
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.setAttribute('href', url);
        link.setAttribute('download', 'table-data.csv');
        link.click();
    }

    private exportReport = () => {
        if (this.isReportReady && this.isDataReady) {
            this.exportToCSV();
        } else {
            this.runReport(true);
        }
    };

    private parseInputFields = (inputsList: FieldUiDataObj[]): FieldUiDataObj[] => {
        let input:FieldUiDataObj[] = [];
        for (let item of inputsList) {
            if (item.type === 'fieldset') {
                const setItem = {...item};
                input.push(setItem);
                for (let subItem of setItem.sublist) {
                    this.parseField(subItem);
                }
                continue;
            }
            if (item.type === 'group') {
                for (let subItem of item.sublist) {
                    const groupItem = {...subItem};
                    this.parseField(groupItem);
                    input.push(groupItem);
                }
                continue;
            }
            const parsedItem = {...item};
            this.parseField(parsedItem);
            input.push(parsedItem);
        }
        return input;
    };

    private parseField = (field: FieldUiDataObj) => {
        const stringValue = 'value';
        const controls = this.form.controls;
        let value = controls[field.name].value;
        if (field.type.toLowerCase() === 'daterange'
            || field.type.toLowerCase() === 'comparativedaterange') {
            field.sublist[1][stringValue] = this.toYYYYMMDDFromMoment(controls[field.name + '_from'].value);
            field.sublist[2][stringValue] = this.toYYYYMMDDFromMoment(controls[field.name + '_to'].value);
            field[stringValue] = 'custom';
        } else if (value && (field.type === 'numeric'
            || field.type === 'dropdown')) {
            field[stringValue] = value.toString();
        } else if (value && (field.type === 'datepicker')) {
            field[stringValue] = this.toYYYYMMDDFromMoment(value);
        } else if (field.type === 'list') {
            if (value?.length > 0) {
                field[stringValue] = value[0].id;
            } else {
                field[stringValue] = null;
            }
        } else if (value && value.id) {
            field[stringValue] = value.id;
        } else if (typeof value === 'string') {
            const trimmed = value.trim();
            field[stringValue] = trimmed === '' ? null : trimmed;
        }

        if (!value || field[stringValue] === '') {
            field[stringValue] = null;
        }
    };

    private toYYYYMMDDFromMoment = (input: Moment): string => {
        return input ? input.utc(true).format(MOMENT_DATE_FORMAT_DAY) : '';
    };

    private updateControls(updateCampaigns: boolean, reportId) {
        if (updateCampaigns) {
            let campaigns = this.reportCampaigns.get(String(reportId));
            this.currentCampaigns = campaigns ? [...campaigns] : [];
            if (this.currentCampaigns.length > 0) {
                this.selectedCampaignControl.setValue(this.currentCampaigns[0]);
            } else {
                this.selectedCampaignControl.setValue(undefined);
            }
        }
        this.currentCampaigns.length > 1 ? this.selectedCampaignControl.enable() : this.selectedCampaignControl.disable();
        this.reports.length > 1 ? this.selectedReportControl.enable() : this.selectedReportControl.disable();
    }
}
